import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { PageContainer } from '../../../components/PageContainer';
import { useGetCamerasQuery } from '../../cameras/api/camerasApi';
import { useSelector } from 'react-redux';
import { authSelector } from '../../auth/state/authSlice';
import { FullScreenSpinner } from '../../../components/Spinner/Spinner';
import { useLivestreamingSetup } from '../hooks/useLivestreamingSetup';
import { 
  CameraSelectionStep, 
  ConfigurationStep, 
  SuccessStep, 
  StatusModal 
} from '../components';

export const LivestreamingSetup = () => {
  const { token } = useSelector(authSelector);
  const { data: camerasData, isLoading: loadingCameras } = useGetCamerasQuery(undefined, { skip: !token });
  
  // Modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // Use custom hook for livestreaming logic
  const {
    setupData,
    selectedCamera,
    setupStep,
    isSettingUpCamera,
    cameraSetupComplete,
    setupProgress,
    isConfiguringRtsp,
    rtspConfigProgress,
    setSelectedCamera,
    handleInputChange,
    handleCameraSetup,
    handleRtspConfiguration
  } = useLivestreamingSetup();

  // Note: Scroll handling is now managed in SuccessStep component to avoid conflicts

  // Filter cameras to only show Heavy Streaming plan cameras
  const heavyStreamingCameras = camerasData?.cameras.filter(camera => {
    const productName = camera.stripeSubscription?.productName || '';
    return productName.toLowerCase().includes('heavy') && camera.SubStatus === 'active';
  }) || [];

  const handleCameraSetupWithModal = async () => {
    const result = await handleCameraSetup();
    if (!result.success) {
      setModalMessage(result.message || 'Camera setup failed');
      setShowErrorModal(true);
    }
    // On success, just continue to next step (no modal needed)
  };

  const handleRtspConfigurationWithModal = async () => {
    const result = await handleRtspConfiguration();
    if (!result.success) {
      setModalMessage(result.message || 'RTSP configuration failed');
      setShowErrorModal(true);
    }
    // Note: Scroll handling is now managed in SuccessStep component to avoid conflicts
  };

  if (loadingCameras) {
    return (
      <PageContainer title="Setup Livestreaming" onGoBack="/dashboard">
        <Container>
          <FullScreenSpinner />
        </Container>
      </PageContainer>
    );
  }

  const getPageTitle = () => {
    switch (setupStep) {
      case 'select': return 'Setup Livestreaming';
      case 'configure': return 'Configure Livestreaming';
      case 'submitted': return 'Livestream Active';
      default: return 'Setup Livestreaming';
    }
  };

  const getSubtitle = () => {
    switch (setupStep) {
      case 'select': return 'Choose one of your Heavy Streaming cameras to configure for livestreaming. This is included in your Heavy Streaming plan and will give you the ability to showcase your camera feed live on your website.';
      case 'configure': return 'Complete the configuration by setting your streaming schedule and preferences.';
      default: return '';
    }
  };

  return (
    <PageContainer title={getPageTitle()} onGoBack="/dashboard">
      <Container>
        {setupStep !== 'submitted' && (
          <Header>
            <Title>{setupStep === 'select' ? 'Select Camera to Setup' : 'Configure Your Livestream'}</Title>
            <Subtitle>{getSubtitle()}</Subtitle>
          </Header>
        )}

        {setupStep === 'select' && (
          <CameraSelectionStep
            heavyStreamingCameras={heavyStreamingCameras}
            selectedCamera={selectedCamera}
            onCameraSelect={setSelectedCamera}
            onCameraSetup={handleCameraSetupWithModal}
            isSettingUpCamera={isSettingUpCamera}
            setupProgress={setupProgress}
          />
        )}

        {setupStep === 'configure' && (
          <ConfigurationStep
            selectedCamera={selectedCamera}
            setupData={setupData}
            cameraSetupComplete={cameraSetupComplete}
            isConfiguringRtsp={isConfiguringRtsp}
            rtspConfigProgress={rtspConfigProgress}
            onInputChange={handleInputChange}
            onRtspConfiguration={handleRtspConfigurationWithModal}
          />
        )}

        {setupStep === 'submitted' && (
          <SuccessStep
            selectedCamera={selectedCamera}
            setupData={setupData}
          />
        )}
      </Container>
      
      {/* Error Modal */}
      <StatusModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        type="error"
        title="Setup Failed"
        message={modalMessage}
      />
    </PageContainer>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
  line-height: 1.5;
`;
